import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { BrandsService } from '../brands.service'; 
import { Brand } from '../brands.model';
import { BusinessService } from '../../business/business.service';
import { EntityDropdownPickerComponent } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { Business } from '../../business/business.model';
import { CardModule } from 'primeng/card';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { IftaLabelModule } from 'primeng/iftalabel';

@Component({
  selector: 'app-brands-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    CardModule,
    ToastModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    EntityDropdownPickerComponent
  ],
  templateUrl: './brands-form.component.html',
  providers: [MessageService]
})
export class BrandFormComponent implements OnInit {
  brandId: number | null = null;
  brandForm!: FormGroup;
  isEditMode = false;
  pageTitle = 'Create New Brand';
  businessPickerAdapter!: EntityDropdownPickerAdapter<Business>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,      
    private brandsService: BrandsService,
    private messageService: MessageService,
    private businessService: BusinessService,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.businessPickerAdapter = this.businessService.createBusinessPickerAdapter();
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.brandId = +id;
        this.isEditMode = true;
        this.pageTitle = 'Edit Brand';
        this.loadBrandData(this.brandId); 
      }
    });
  }

  initForm(): void {
    this.brandForm = this.fb.group({
      brandName: ['', Validators.required],
      brandCode: [''],
      business:[null,Validators.required]
    });
  }

  loadBrandData(id: number): void {
    this.brandsService.getBrandById(id).subscribe({
      next: (brand) => {
        this.brandForm.patchValue(brand);
        if (brand.businessId) {
          this.businessService.getBusinessById(brand.businessId).subscribe({
            next: (business) => this.brandForm.patchValue({ business }),
          });
        }
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load business unit data.' });
        console.error(err);
      }
    });
  }

  saveBrand(): void {
    if (this.brandForm.invalid) {
      this.brandForm.markAllAsTouched(); 
      return;
    }

    const formValue = this.brandForm.value;
    const brandData: Brand = {
      id: this.brandId ?? 0,
      brandName: formValue.brandName,
      brandCode: formValue.brandCode,
      businessId: formValue.business?.id,
    };
    const operation = this.isEditMode
      ? this.brandsService.updateBrand(brandData)
      : this.brandsService.createBrand(brandData);

    operation.subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `Brand ${this.isEditMode ? 'updated' : 'created'} successfully.` });
        setTimeout(() => this.router.navigate(['/ai-studio/session/current', { outlets: { canvasOutlet: ['brand', 'list'] } }]), 1500);
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: `Failed to ${this.isEditMode ? 'update' : 'create'} branch.` });
        console.error(err);
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/ai-studio/session/current', { outlets: { canvasOutlet: ['brand', 'list'] } }]);
  }
}
